import {
  Button,
  paletteHelper,
  spacingHelper,
  typographyHelper,
  mediaHelper,
} from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import type { PropertyTimelineEventGroupProps } from './subcomponent/PropertyTimelineEventGroup/PropertyTimelineEventGroup';
import {
  EventDetailsWrapper,
  PropertyTimelineEventGroup,
  TimelineEventWrapper,
  timelineDateColumnWidth,
} from './subcomponent/PropertyTimelineEventGroup/PropertyTimelineEventGroup';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { mapApiBadgeVariantToBadgeVariant } from '../../../app-kit/helpers/mapApiBadgeVariantToBadgeVariant';
import { mapApiWeblinkVariantToLinkVariant } from '../../../app-kit/helpers/mapApiWeblinkVariantToLinkVariant';
import { mapApiWeblinkIconInfoToLinkIconInfo } from '../../../app-kit/helpers/mapApiWeblinkIconInfoToLinkIconInfo';
import { PropertyTimelineBeforeYouBuyWithData } from './subcomponent/PropertyTimelineBeforeYouBuy/PropertyTimelineBeforeYouBuyWithData';
import { ProptrackLogoBrickSource } from '../../../components/ProptrackLogoBrickSource/ProptrackLogoBrickSource';

const TimelineBody = styled.div`
  position: relative;
  margin-top: ${spacingHelper('twoExtraSmall')};

  /* required so line does not show in last event */
  ${TimelineEventWrapper}:last-child {
    background-color: ${paletteHelper('backgroundPrimaryBase')};

    /* required so margin bottom does not show in last event timeline */
    ${EventDetailsWrapper}:last-child {
      > * {
        margin-bottom: unset;
      }
    }
  }

  ${mediaHelper('tabletMin')} {
    margin-left: ${spacingHelper('medium')};
  }

  margin-bottom: ${spacingHelper('extraLarge')};
`;

const Line = styled.div`
  position: absolute;
  width: 2px;
  top: calc(${spacingHelper('small')} / 2);
  bottom: 28px;
  left: calc(${timelineDateColumnWidth}px - ${spacingHelper('extraSmall')} / 2 - 1px);
  background-color: ${paletteHelper('borderSecondary')};
`;

const Disclaimer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const DisclaimerLinkButton = styled(Button).attrs(() => ({ variant: 'link-primary' }))`
  ${typographyHelper('caption')}
  text-align: left;
`;

interface PropertyTimeline {
  title: string;
  subtitle: string;
  eventGroups: PropertyTimelineEventGroupProps[];
}

export const PropertyTimelineEvents: FunctionComponent<PropertyTimeline> = ({
  title,
  subtitle,
  eventGroups,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  const hasMoreThenOneEvent = !(
    (eventGroups.length === 1 || eventGroups.length === 0) &&
    eventGroups[0]?.events.length === 1
  );

  return (
    <ContentBrick title={title} subtitle={subtitle}>
      <TimelineBody>
        {hasMoreThenOneEvent && <Line data-testid="property-timeline-line" />}
        {eventGroups.map((eventGroup) => (
          <PropertyTimelineEventGroup key={eventGroup.label} {...eventGroup} />
        ))}
      </TimelineBody>
      <PropertyTimelineBeforeYouBuyWithData />
      <Disclaimer>
        <ProptrackLogoBrickSource />
        <DisclaimerLinkButton
          onClick={() => {
            displayModal({ name: 'propertyPageDataDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_timeline_events___disclaimer_link',
            });
          }}
        >
          About this data
        </DisclaimerLinkButton>
      </Disclaimer>
    </ContentBrick>
  );
};

export const PropertyTimelineEventsWithData: FunctionComponent = () => {
  const timelineData = usePropertyPageQuery()?.data.propertyPageBySlugV3.timelineV4;
  if (!timelineData?.eventGroups?.length) return null;

  const { title, subtitle, eventGroups } = timelineData;
  const mappedEventGroups = eventGroups.map((eventGroup) => ({
    ...eventGroup,
    events: eventGroup.events.map((event) => ({
      ...event,
      links: event.linksV2?.map((link) => ({
        ...link,
        variant: mapApiWeblinkVariantToLinkVariant(link.variant),
        iconInfo: link.iconInfo ? mapApiWeblinkIconInfoToLinkIconInfo(link.iconInfo) : null,
      })),
      title: event.titleV2,
      label: eventGroup.label,
      badge: event.badgeV3
        ? {
            text: event.badgeV3?.text,
            variant: mapApiBadgeVariantToBadgeVariant(event.badgeV3?.variant),
          }
        : null,
      media: event.media
        ? {
            ...event.media,
            mediaCounts: event.media?.mediaCounts.map((mediaCount) => ({
              ...mediaCount,
              iconName: mediaCount.iconNameMd,
            })),
          }
        : null,
    })),
  }));

  return (
    <PropertyTimelineEvents title={title} eventGroups={mappedEventGroups} subtitle={subtitle} />
  );
};
